import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Plus,
  Search,
  Calendar,
  ChevronDown,
  Check,
  User,
  AlertTriangle,
  FileText,
  PanelRightClose,
  ShieldAlert,
  Tag,
  BookOpen,
  Hash,
} from "lucide-react";
import api from "../../connector/api";
import { toast } from "react-hot-toast";
import LogUniformInfractionModal from "../components/LogUniformInfractionModal";

/* Custom Term Filter Dropdown Component */
const CustomTermDropdown = ({ terms, selectedTerm, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const getTermLabel = (t) => {
    if (!t) return "";
    if (t.id === "all") return "All Terms";

    const termName = t.term_detail?.name || t.term?.name || t.name || t.term_name;
    const yearName = t.school_year_detail?.name || t.school_year?.name || t.school_year_name;

    if (yearName && termName) {
      return `${yearName} - ${termName}`;
    }

    return termName || yearName || `Term ${t.id}`;
  };

  const selectedOption =
    selectedTerm === "all"
      ? { id: "all", name: "All Terms" }
      : terms.find((t) => String(t.id) === String(selectedTerm));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="h-8 border border-zinc-200 rounded-lg px-2.5 text-xs font-medium bg-zinc-50 hover:bg-zinc-100 text-zinc-800 flex items-center gap-2 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
      >
        <Calendar className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="truncate max-w-[200px]">
          {selectedOption ? getTermLabel(selectedOption) : "Select Term"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-20 w-64 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1 text-xs [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400">
          <button
            type="button"
            onClick={() => {
              onChange("all");
              setIsOpen(false);
            }}
            className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
              selectedTerm === "all"
                ? "bg-blue-50 text-blue-700 font-semibold"
                : "text-zinc-700"
            }`}
          >
            <span>All Terms</span>
            {selectedTerm === "all" && (
              <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            )}
          </button>
          {terms.map((t) => {
            const isSelected = String(selectedTerm) === String(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onChange(t.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
                  isSelected
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-zinc-700"
                }`}
              >
                <span className="truncate">{getTermLabel(t)}</span>
                {isSelected && (
                  <Check className="w-3.5 h-3.5 text-blue-600 shrink-0 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* Independent Height Student Card Component displaying all UniformInfraction fields */
const DressCodeCard = ({ student, onLogIncident, onEditIncident }) => {
  const records = student.dress_code_records || [];
  const displayName =
    student.name ||
    `${student.last_name || ""}, ${student.first_name || ""}`.trim() ||
    "Unknown Student";

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-2xs overflow-hidden hover:border-zinc-300 transition-all">
      {/* Student Card Header */}
      <div className="p-3.5 border-b border-zinc-100 bg-zinc-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div className="w-7 h-7 rounded-full bg-zinc-200 border border-zinc-300 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-zinc-600" />
          </div>
          <div className="truncate">
            <h3 className="font-bold text-zinc-900 text-xs truncate">
              {displayName}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
            {records.length} {records.length === 1 ? "Infraction" : "Infractions"}
          </span>
          <button
            type="button"
            onClick={() => onLogIncident(student)}
            className="h-7 px-2.5 bg-blue-50 hover:bg-blue-100 font-mono text-blue-700 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-colors cursor-pointer"
          >
            <Plus className="w-3 h-3" />
            <span>Log</span>
          </button>
        </div>
      </div>

      {/* Dynamic Height Records List */}
      <div className="divide-y divide-zinc-100">
        {records.map((rec) => {
          const specificItem = rec.specific_item || rec.violation_type || "Uniform Infraction";
          const date = rec.date_of_infraction || rec.date_of_incident;
          const loggedBy = rec.logged_by_name || rec.logged_by;
          const subject = rec.subject_name;
          const term = rec.school_year_term_name;
          const cumulativeCount = rec.cumulative_count;
          const detailedNote = rec.detailed_note || rec.incident_description;
          const intervention = rec.intervention_applied || rec.action_taken;

          return (
            <div
              key={rec.id}
              className="p-3 space-y-2.5 hover:bg-zinc-50/50 transition-colors group relative"
            >
              {/* Header Row: Specific Item Badge & Date / Edit */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                    <Tag className="w-3 h-3 shrink-0" />
                    {specificItem}
                  </span>

                  {/* Cumulative Count Badge */}
                  {cumulativeCount !== undefined && cumulativeCount !== null && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                      <Hash className="w-2.5 h-2.5 shrink-0 text-zinc-400" />
                      Count: {cumulativeCount}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                  {date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-zinc-400" />
                      {formatDate(date)}
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => onEditIncident(student, rec)}
                    className="ml-1 inline-flex items-center font-mono gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                  >
                    <span>Edit</span>
                  </button>
                </div>
              </div>

              {/* Detailed Note */}
              {detailedNote && (
                <div className="flex items-start gap-1.5 text-xs text-zinc-700">
                  <FileText className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed break-words">
                    {detailedNote}
                  </p>
                </div>
              )}

              {/* Metadata Footer: Intervention, Logged By, Subject, and Term */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-zinc-500 pt-1 border-t border-zinc-100/70">
                {/* Intervention Applied */}
                {intervention && (
                  <div className="flex items-center gap-1 text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded capitalize">
                    <ShieldAlert className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>Action: {intervention}</span>
                  </div>
                )}

                {/* Logged By */}
                {loggedBy && (
                  <div className="flex items-center gap-1 text-zinc-400">
                    <User className="w-3 h-3 shrink-0" />
                    <span>Logged by: {loggedBy}</span>
                  </div>
                )}

                {/* Subject Name */}
                {subject && (
                  <div className="flex items-center gap-1 text-zinc-400">
                    <BookOpen className="w-3 h-3 shrink-0" />
                    <span>{subject}</span>
                  </div>
                )}

                {/* Term Name */}
                {term && (
                  <div className="flex items-center gap-1 text-zinc-400">
                    <Calendar className="w-3 h-3 shrink-0" />
                    <span>{term}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DressCodeTab = ({
  activeClass,
  activeStudents = [],
  selectedSubjectId,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTerm, setSelectedTerm] = useState("all");
    const [terms, setTerms] = useState([]);

    // API State
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiError, setApiError] = useState(null);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);
    const [selectedRecordForModal, setSelectedRecordForModal] = useState(null);

    useEffect(() => {
        const fetchTerms = async () => {
        try {
            const response = await api.get("year-terms/");
            const data = response.data.results || response.data;
            setTerms(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to load terms for dress code filter:", err);
        }
        };
        fetchTerms();
    }, []);

    const fetchDressCodeRecords = useCallback(async () => {
      if (!activeClass?.id) return;
      setLoading(true);
      setError(null);

      try {
      const response = await api.get("teacher/uniform-infractions/", {
          params: {
          classroom: activeClass.id,
          // Removed: all_dates: 'true' to avoid requesting historical records
          },
      });
      const data = response.data.results || response.data || [];
      setRecords(Array.isArray(data) ? data : []);
      } catch (err) {
      console.error("Failed to fetch dress code records:", err);
      setError("Failed to load dress code records.");
      } finally {
      setLoading(false);
      }
    }, [activeClass?.id]);

    useEffect(() => {
        fetchDressCodeRecords();
    }, [fetchDressCodeRecords]);

    // Extract today's logged infractions to prevent duplicates in the modal
    const todayInfractions = useMemo(() => {
        const todayStr = new Date().toISOString().slice(0, 10);
        return records.filter((rec) => {
        const recDate = rec.date_of_infraction || rec.date_of_incident;
        if (!recDate) return false;
        return new Date(recDate).toISOString().slice(0, 10) === todayStr;
        });
    }, [records]);

    // Optimized student filter calculation
    // Filter students to include ONLY infractions from TODAY
  const studentsWithRecords = useMemo(() => {
      const query = searchQuery.trim().toLowerCase();

      // Get today's date in YYYY-MM-DD format
      const todayStr = new Date().toISOString().split("T")[0];

      // First filter raw records to include ONLY today's date
      const todayRecords = records.filter((rec) => {
      const recDate = rec.date_of_infraction || rec.date_of_incident;
      if (!recDate) return false;
      
      // Safely parse ISO strings or Date strings to YYYY-MM-DD
      const formattedRecDate = new Date(recDate).toISOString().split("T")[0];
      return formattedRecDate === todayStr;
      });

      return activeStudents
      .map((student) => {
          const studentIdStr = String(student.id || student.student_id);

          const studentViolations = todayRecords.filter((rec) => {
          const recStudentId = String(
              typeof rec.student === "object" ? rec.student?.id : rec.student
          );
          return recStudentId === studentIdStr;
          });

          return {
          ...student,
          dress_code_records: studentViolations,
          };
      })
      .filter((st) => {
          const recs = st.dress_code_records;
          if (!recs || recs.length === 0) return false;

          const studentName = `${st.first_name || ""} ${st.last_name || ""} ${
          st.name || ""
          }`.toLowerCase();

          const matchesSearch = !query || studentName.includes(query);

          const matchesTerm =
          selectedTerm === "all" ||
          recs.some((rec) => {
              const termId =
              typeof rec.school_year_term === "object"
                  ? rec.school_year_term?.id
                  : rec.school_year_term;
              return String(termId) === String(selectedTerm);
          });

          return matchesSearch && matchesTerm;
      });
  }, [activeStudents, records, searchQuery, selectedTerm]);

    const handleOpenModal = (student = null) => {
        setApiError(null);
        setSelectedStudentForModal(student);
        setSelectedRecordForModal(null);
        setIsModalOpen(true);
    };

    const handleEditIncident = (student, record) => {
        setApiError(null);
        setSelectedStudentForModal(student);
        setSelectedRecordForModal(record);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedStudentForModal(null);
        setSelectedRecordForModal(null);
        setApiError(null);
    };

  // Submit handler passed to LogUniformInfractionModal
    const handleSaveInfraction = async (formData) => {
        setApiError(null);
        try {
            if (formData.id) {
            // Update existing infraction
            await api.patch(`teacher/uniform-infractions/${formData.id}/`, formData);
            toast.success("Uniform infraction updated successfully.");
            } else {
            // Create new infraction
            await api.post("teacher/uniform-infractions/", formData);
            toast.success("Uniform infraction logged successfully.");
            }
            handleCloseModal();
            fetchDressCodeRecords();
        } catch (err) {
            console.error("Failed to save uniform infraction:", err);
            const errMsg =
            err.response?.data?.non_field_errors?.[0] ||
            err.response?.data?.detail ||
            err.response?.data?.error ||
            "An infraction record already exists for this student today.";
            
            setApiError(errMsg);
            toast.error(errMsg); // Show toast error notification
        }
    };

    // Clear/Delete handler passed to LogUniformInfractionModal
    const handleClearInfraction = async (infractionId) => {
        try {
            await api.delete(`teacher/uniform-infractions/${infractionId}/`);
            toast.success("Uniform infraction removed.");
            handleCloseModal();
            fetchDressCodeRecords();
        } catch (err) {
            console.error("Failed to delete uniform infraction:", err);
            toast.error("Failed to delete uniform infraction.");
        }
    };

    return (
        <div className="flex-1 p-4 space-y-4 w-full flex flex-col h-auto min-h-0 bg-zinc-100">
        {/* Control Bar: Search Input, Term Filter, Log Note & Summary Sidebar Toggle */}
        <div className="flex items-center gap-3 bg-white border border-zinc-200 rounded-lg p-2.5 shadow-2xs">
            <div className="flex-1 flex items-center gap-2 border-r border-zinc-200 pr-3">
            <Search className="w-4 h-4 text-zinc-400 shrink-0" />
            <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search students with records..."
                className="w-full bg-transparent text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none"
            />
            </div>

            {/* Term Dropdown Filter */}
            <CustomTermDropdown
            terms={terms}
            selectedTerm={selectedTerm}
            onChange={setSelectedTerm}
            />

            {/* Log Dress Code Note Button */}
            <button
            type="button"
            onClick={() => handleOpenModal(null)}
            className="h-8 px-3 bg-[#002855] hover:bg-blue-900 font-mono text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
            >
            <Plus className="w-3.5 h-3.5" />
            <span>Log Dress Code</span>
            </button>

            {typeof setIsRightSidebarOpen === "function" && !isRightSidebarOpen && (
            <button
                type="button"
                onClick={() => setIsRightSidebarOpen(true)}
                className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 font-mono rounded-lg text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0 cursor-pointer"
                title="Open Sidebar"
            >
                <PanelRightClose className="w-4 h-4 text-zinc-600" />
                <span>Summary</span>
            </button>
            )}
        </div>

        {/* Cards Grid Container */}
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400">
            {loading ? (
            <div className="p-12 text-center text-zinc-400 text-xs bg-white border border-zinc-200 rounded-lg">
                Loading dress code records...
            </div>
            ) : error ? (
            <div className="p-12 text-center text-rose-500 text-xs bg-white border border-zinc-200 rounded-lg">
                {error}
            </div>
            ) : studentsWithRecords.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 items-start">
                {studentsWithRecords.map((student, idx) => (
                <DressCodeCard
                    key={student.id || idx}
                    student={student}
                    onLogIncident={handleOpenModal}
                    onEditIncident={handleEditIncident}
                />
                ))}
            </div>
            ) : (
            <div className="p-12 text-center text-zinc-400 text-xs bg-white border border-zinc-200 rounded-lg">
                {searchQuery || selectedTerm !== "all"
                ? "No student dress code records match your criteria."
                : "No active student dress code records found for this classroom."}
            </div>
            )}
        </div>

        {/* Dress Code Modal */}
        {isModalOpen && (
            <LogUniformInfractionModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSubmit={handleSaveInfraction}
            onClear={handleClearInfraction}
            students={activeStudents}
            selectedStudentId={selectedStudentForModal?.id || ""}
            selectedSubjectId={selectedSubjectId || ""}
            existingInfraction={selectedRecordForModal}
            existingTodayInfractions={todayInfractions}
            apiError={apiError}
            />
        )}
        </div>
    );
};

export default DressCodeTab;